/**
 * Skin runtime (theme spec S0.5): loads the layers of the active mode,
 * applies its tokens, and gives screens the mode's wording, chrome, keymap
 * and variant slots.
 *
 *   useSkin()           the active mode and its layers
 *   useTerm(key)        a label in the active mode's wording
 *   <Term k="..." />    the same, as an element
 *   useSkinSlot(name)   the mode's variant component for a slot, or undefined
 *   <SkinSlot ...>      renders the variant around (or instead of) the default
 *   <SkinScope skin>    a scoped preview (Settings > Appearance cards)
 *
 * Without a provider every hook answers for Velora Modern, so module unit
 * tests need no setup.
 */
import {
  DEFAULT_SKIN,
  SKIN_DEFAULT_MODE,
  type AppearanceMode,
  type SkinId,
  type SkinLayout,
  type SkinOverrides,
} from '@velora/contracts/skins';
import { resolveTerm, type TermKey, type TermMap } from '@velora/contracts/skins/terms';
import { ensureSkinStylesheet } from '@velora/ui';
import type { ClinicalPreset, Density } from '@velora/tokens';
import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { loadedSkin, loadSkin, VELORA_SKIN } from './registry.js';
import type {
  ClassicChrome,
  Keymap,
  ScheduleSkinOptions,
  SkinDefinition,
  SkinSlots,
  SlotComponent,
  SlotModels,
  SlotName,
} from './types.js';

export interface SkinInputs {
  skin: SkinId;
  overrides?: SkinOverrides | undefined;
  mode: AppearanceMode;
}

export interface SkinValue {
  /** The mode the user is in. */
  skin: SkinId;
  /** Which skin supplies each layer (differs from `skin` only with overrides). */
  layers: { tokens: SkinId; layout: SkinLayout; terms: SkinId; keymap: SkinId };
  mode: AppearanceMode;
  layout: SkinLayout;
  /** Classic chrome, `null` in the modern layout. */
  chrome: ClassicChrome | null;
  terms: TermMap;
  keymap: Keymap;
  slots: SkinSlots;
  /** Schedule grid behaviour; `{}` (Velora's) in the modern layout. */
  schedule: ScheduleSkinOptions;
  clinicalPreset: ClinicalPreset;
  density: Density | undefined;
  /** A layer is still loading; the previous mode stays applied meanwhile. */
  loading: boolean;
}

function compose(
  skin: SkinId,
  overrides: SkinOverrides | undefined,
  mode: AppearanceMode,
  get: (id: SkinId) => SkinDefinition | undefined,
): SkinValue | null {
  const ids = {
    skin,
    tokens: overrides?.tokens ?? skin,
    terms: overrides?.terms ?? skin,
    keymap: overrides?.keymap ?? skin,
  };
  const main = get(ids.skin);
  const tokens = get(ids.tokens);
  const terms = get(ids.terms);
  const keymap = get(ids.keymap);
  if (!main || !tokens || !terms || !keymap) return null;
  // A classic layout needs chrome; Velora has none, so it stays modern.
  const wanted = overrides?.layout ?? main.layout;
  const layout: SkinLayout = wanted === 'classic' && main.chrome ? 'classic' : 'modern';
  return {
    skin,
    layers: { tokens: ids.tokens, layout, terms: ids.terms, keymap: ids.keymap },
    mode,
    layout,
    chrome: layout === 'classic' ? (main.chrome ?? null) : null,
    terms: terms.terms,
    keymap: keymap.keymap,
    slots: layout === 'classic' ? main.slots : {},
    schedule: layout === 'classic' ? (main.schedule ?? {}) : {},
    clinicalPreset: tokens.clinicalPreset,
    density: tokens.density,
    loading: false,
  };
}

export const VELORA_VALUE: SkinValue = compose(
  DEFAULT_SKIN,
  undefined,
  SKIN_DEFAULT_MODE.velora,
  (id) => (id === 'velora' ? VELORA_SKIN : undefined),
) as SkinValue;

const SkinContext = createContext<SkinValue>(VELORA_VALUE);

/** Loads what `inputs` needs and returns the composed value (the last good one while loading). */
export function useSkinValue(inputs: SkinInputs): SkinValue {
  const { skin, overrides, mode } = inputs;
  const [, setTick] = useState(0);
  const last = useRef<SkinValue>(VELORA_VALUE);
  const key = JSON.stringify([skin, overrides ?? null]);

  const ready = compose(skin, overrides, mode, loadedSkin);

  useEffect(() => {
    let live = true;
    const ids = new Set<SkinId>([
      skin,
      overrides?.tokens ?? skin,
      overrides?.terms ?? skin,
      overrides?.keymap ?? skin,
    ]);
    const missing = [...ids].filter((id) => !loadedSkin(id));
    if (missing.length === 0) return undefined;
    Promise.all(missing.map(loadSkin)).then(
      () => {
        if (live) setTick((t) => t + 1);
      },
      (err: unknown) => {
        // Stay on the current mode; the next selection retries.
        console.error('skin failed to load', err);
      },
    );
    return () => {
      live = false;
    };
    // `key` captures skin and overrides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (ready) {
    last.current = ready;
    return ready;
  }
  return { ...last.current, mode, loading: true };
}

/** Provides an already composed value (the app's prefs layer owns the inputs). */
export function SkinProvider({ value, children }: { value: SkinValue; children: ReactNode }) {
  const tokensId = value.layers.tokens;
  // Layout effect: the stylesheet lands in the same frame as data-skin.
  useLayoutEffect(() => {
    const def = loadedSkin(tokensId);
    if (def) ensureSkinStylesheet(tokensId, def.tokens);
  }, [tokensId]);
  return <SkinContext.Provider value={value}>{children}</SkinContext.Provider>;
}

/**
 * A preview of another mode inside the page: its own data-skin, data-theme
 * and data-clin on a wrapper, and its own wording for everything inside.
 * Portalled overlays still follow the document's mode.
 */
export function SkinScope({
  skin,
  mode,
  className,
  children,
}: {
  skin: SkinId;
  mode?: AppearanceMode;
  className?: string;
  children: ReactNode;
}) {
  const value = useSkinValue({ skin, mode: mode ?? SKIN_DEFAULT_MODE[skin] });
  const ready = value.skin === skin && !value.loading;
  useLayoutEffect(() => {
    const def = loadedSkin(skin);
    if (ready && def) ensureSkinStylesheet(skin, def.tokens);
  }, [skin, ready]);
  return (
    <SkinContext.Provider value={value}>
      <div
        className={className}
        data-skin={skin}
        data-theme={value.mode}
        data-clin={value.clinicalPreset}
        data-layout={value.layout}
        aria-busy={ready ? undefined : true}
      >
        {children}
      </div>
    </SkinContext.Provider>
  );
}

/* ------------------------------------------------------------------ hooks */

export function useSkin(): SkinValue {
  return useContext(SkinContext);
}

/** A label in the active mode's wording (falls back to Velora's). */
export function useTerm(key: TermKey): string {
  return resolveTerm(useContext(SkinContext).terms, key);
}

/** A lookup function, for lists and for labels that may be term keys or literal text. */
export function useTerms(): (key: TermKey) => string {
  const { terms } = useContext(SkinContext);
  return useMemo(() => (key: TermKey) => resolveTerm(terms, key), [terms]);
}

export function Term({ k }: { k: TermKey }) {
  return <>{useTerm(k)}</>;
}

/** The active mode's schedule grid behaviour (`{}` = Velora's). */
export function useScheduleSkin(): ScheduleSkinOptions {
  return useContext(SkinContext).schedule;
}

export function useSkinLayout(): SkinLayout {
  return useContext(SkinContext).layout;
}

/** The active mode's component for `name`, or undefined (render the default). */
export function useSkinSlot<N extends SlotName>(name: N): SlotComponent<N> | undefined {
  return useContext(SkinContext).slots[name] as SlotComponent<N> | undefined;
}

/**
 * Renders the active mode's variant for `name` with the default render as
 * its children, or just the default. Pass `model` as a function when it is
 * costly to build: it is only called when a variant exists.
 */
export function SkinSlot<N extends SlotName>({
  name,
  model,
  children,
}: {
  name: N;
  model: SlotModels[N] | (() => SlotModels[N]);
  children: ReactNode;
}) {
  const Variant = useSkinSlot(name);
  if (!Variant) return <>{children}</>;
  const m = typeof model === 'function' ? (model as () => SlotModels[N])() : model;
  return (
    <Suspense fallback={children}>
      <Variant model={m}>{children}</Variant>
    </Suspense>
  );
}
